import React from "react";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import "./WaterPressureCard.css";

/**
 * Presión de la línea, en psi.
 *
 * Antes era un gauge radial que ocupaba media tarjeta para comunicar un solo
 * número. Ahora la cifra manda y la escala va debajo, en una barra fina que
 * sitúa la lectura entre las tres zonas reales del sistema: baja (<30),
 * óptima (30-75) y alta (>75). Los umbrales no cambian.
 *
 * `variant="inline"` la deja sin relleno propio para incrustarla en otra
 * composición; `card` es la celda que usa la tira de métricas del panel.
 */
export default function WaterPressureCard({
  pressure = 0,
  min = 0,
  max = 100,
  unit = "psi",
  trend = null,
  valveClosed = false,
  compact = false,
  variant = "card", // "card" | "inline"
}) {
  // `null` significa que el dispositivo no mide presión: no es lo mismo que
  // medir cero. Mostrar 0 psi haría creer que no hay agua en la tubería.
  const sinDato =
    pressure === null || pressure === undefined || !Number.isFinite(Number(pressure));
  const value = sinDato ? 0 : Number(pressure);
  const clamped = Math.max(min, Math.min(value, max));
  const pct = ((clamped - min) / (max - min)) * 100;

  let status = "normal";
  let statusLabel = "Presión óptima";

  if (valveClosed) {
    status = "closed";
    statusLabel = "Válvula cerrada";
  } else if (sinDato) {
    status = "unknown";
    statusLabel = "Sin sensor de presión";
  } else if (value < 30) {
    status = "low";
    statusLabel = "Presión baja";
  } else if (value > 75) {
    status = "high";
    statusLabel = "Presión alta";
  }

  return (
    <div
      className={`pressure-card pressure-card--${variant} pressure-card--${status} ${
        compact ? "is-compact" : ""
      }`}
    >
      <div className="pressure-card__head">
        <span className="pressure-card__label">Presión del agua</span>
        <span className="pressure-card__icon">
          <SpeedRoundedIcon />
        </span>
      </div>

      <div className="pressure-card__value">
        <span className="pressure-card__num">{sinDato ? "—" : value.toFixed(1)}</span>
        {!sinDato && <span className="pressure-card__unit">{unit}</span>}
      </div>

      {/* Escala con las dos fronteras reales marcadas. Sin dato no se pinta
          posición: una marca en el cero diría "presión cero", que es falso. */}
      <div
        className="pressure-card__scale"
        role="img"
        aria-label={
          valveClosed
            ? "Válvula cerrada; sin paso de agua"
            : sinDato
            ? "Sin lectura de presión"
            : `${value.toFixed(1)} ${unit}. ${statusLabel}. Rango óptimo de 30 a 75 psi.`
        }
      >
        <div className="pressure-card__track">
          <span className="pressure-card__zone pressure-card__zone--ok" />
          {!sinDato && (
            <span
              className="pressure-card__marker"
              style={{ left: `${pct}%` }}
            />
          )}
        </div>
        <div className="pressure-card__ticks" aria-hidden="true">
          <span>{min}</span>
          <span>30</span>
          <span>75</span>
          <span>{max}</span>
        </div>
      </div>

      <div className="pressure-card__footer">
        <span className="pressure-card__status">{statusLabel}</span>
        {trend && <span className="pressure-card__trend">{trend}</span>}
      </div>
    </div>
  );
}
